import React, { useEffect } from 'react';
import { isExpired } from 'react-jwt';
import { Navigate, useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import UserService from '../../../services/User.service';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { selectAuth, selectUserInformation, setAuth, setUserInformation } from '../../../store/reducers';
import { Loading } from '../../atoms/Loading/Loading';

type PrivateRouteProps = {
  children?: React.ReactNode;
  requiresActivePatient?: boolean;
};

export function PrivateRoute({ children, requiresActivePatient }: PrivateRouteProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authInfo = useAppSelector(selectAuth);
  const userInformation = useAppSelector(selectUserInformation);

  const checkAuthenticated = () => {
    const token = localStorage.getItem('JWT');
    const user = localStorage.getItem('User');
    const termAgree = localStorage.getItem('TermsAgree');
    const privacyPolicyAgree = localStorage.getItem('PrivacyPolicyAgree');
    const newsletterAgree = localStorage.getItem('NewsletterAgree');
    if (token && user) {
      const decodedToken = jwtDecode(token) as object;
      if (!isExpired(token) || (decodedToken && !('exp' in decodedToken))) {
        const auth = {
          isAuthenticated: true,
          isTermsAgree: termAgree === 'true',
          isPrivacyPolicyAgree: privacyPolicyAgree === 'true',
          isNewsletterAgree: newsletterAgree === 'true',
          name: user,
        };
        dispatch(setAuth(auth));

        UserService.information().then((r) => {
          if (r.error) {
            console.error(r.message);

            if (r.message.response.status === 401) {
              navigate('/login');
            }

            return;
          }

          dispatch(setUserInformation(r.data));
        });
      } else {
        const auth = {
          isAuthenticated: false,
          isTermsAgree: termAgree === 'true',
          isPrivacyPolicyAgree: privacyPolicyAgree === 'true',
          isNewsletterAgree: newsletterAgree === 'true',
        };
        dispatch(setAuth(auth));
        navigate('/login');
      }
    } else {
      const auth = {
        isAuthenticated: false,
        isTermsAgree: termAgree === 'true',
        isPrivacyPolicyAgree: privacyPolicyAgree === 'true',
        isNewsletterAgree: newsletterAgree === 'true',
      };
      dispatch(setAuth(auth));
      navigate('/login');
    }
  };

  useEffect(() => {
    checkAuthenticated();
  }, []);

  if (!authInfo.isAuthenticated || userInformation.id === '') {
    return <Loading />;
  }

  const isPatientDeclined = userInformation?.isPatientDeclined === true;

  if (requiresActivePatient && isPatientDeclined) {
    return <Navigate to='/dashboard' replace />;
  }

  if (children) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return null;
}
